import { useQuery, useQueryClient } from '@tanstack/react-query';
import { registrationApi } from '../api/RegistrationApi';
import { Order } from '../types';
import { StructuredApiError } from '@portal/api/apiErrors';

// Hook to fetch and maintain order state
export const useOrder = (orderToken: string | undefined) => {
    const queryClient = useQueryClient();

    return useQuery<Order, StructuredApiError>({
        queryKey: ['order', orderToken],
        queryFn: () => {
            if (!orderToken) {
                return Promise.reject(new Error("Order token is required"));
            }
            return registrationApi.getOrder(orderToken);
        },
        enabled: !!orderToken,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        refetchOnWindowFocus: true, // Refetch when user returns to tab
        refetchInterval: false, // Don't poll by default
    });
};

// Hook to invalidate order data (useful after mutations)
export const useInvalidateOrder = () => {
    const queryClient = useQueryClient();
    
    return (orderToken: string) => {
        queryClient.invalidateQueries({ queryKey: ['order', orderToken] });
    };
};

// Hook to update order data in cache without refetching
export const useUpdateOrderCache = () => {
    const queryClient = useQueryClient();
    
    return (orderToken: string, updatedOrder: Order) => {
        queryClient.setQueryData(['order', orderToken], updatedOrder);
    };
};